import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { postHomeToniResult } from '../../api/toni_result_api';
import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FieldArray, Form as FormikForm, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { ChildAtHomeToniResultPost, HomeToniResultPost } from '../../typings/types-to-send';
import { Mode, parseMode } from '../../shared/helpers/modeHelper';
import { useTranslation } from 'react-i18next';
import FormikDateInput from '../../shared/formik/FormikDateInput/FormikDateInput';
import FormikSelect, { GenericOption } from '../../shared/formik/FormikSelect/FormikSelect';
import { createChildOptions, useGetChildrenInHome } from '../../api/child_api';
import { BOOLEAN_OPTIONS } from '../../shared/helpers/formHelper';
import { createHomeOptions, useGetHomes } from '../../api/home_api';
import { SimpleChildSummary } from '../../typings/api-types';
import i18n from 'i18next';
import FormikTextInput from '../../shared/formik/FormikTextInput/FormikTextInput';
import { Domain, getDomainPermission, hasPagePermission, usePermissions } from '../../api/permissions_api';
import NoPermissionPage from '../../shared/pages/NoPermissionPage/NoPermissionPage';
import DefaultPageActionButtons from '../../shared/components/DefaultPageActionButtons/DefaultPageActionButtons';
import ErrorPage from '../../shared/pages/ErrorPage/ErrorPage';
import { TONI_DESCRIPTION_OPTIONS } from '../ChildToniResultPage/ChildToniResultPage';
import { CLASS_LEVEL_OPTIONS } from '../../shared/helpers/classLevelHelper';

type HomeToniResultProps = {};

const DOMAIN = Domain.CHILD_TONI_RESULT;

let required_0_to_100_if_truthy = (values: any[], schema: any) => {
  if (values[0]) {
    return schema.min(1).max(100).required('Required');
  } else {
    return schema.nullable().optional();
  }
};

let required_any_if_truthy = (values: any[], schema: any) => {
  if (values[0]) {
    return schema.required('Required');
  } else {
    return schema.nullable().optional();
  }
};

const TONI_RESULT_FORM_SCHEMA = Yup.object().shape({
  home: Yup.number().required('Required'),
  date: Yup.date().required('Required'),

  child_results: Yup.array()
    .of(
      Yup.object().shape({
        // Auto-populated and can't be modified
        child: Yup.number().required('Required'),

        child_was_present: Yup.boolean().required('Required'),

        class_at_time: Yup.number().nullable(),

        raw_score: Yup.number().nullable().optional(),
        index_score: Yup.number().when('child_was_present', required_any_if_truthy),
        percentage_score: Yup.number().when('child_was_present', required_0_to_100_if_truthy),
        description: Yup.string().when('child_was_present', required_any_if_truthy),
      }),
    )
    .required('Required'),
});

function getBlankHomeCheckInNoChildren(): HomeToniResultPost {
  return {
    home: undefined,
    date: '',
    // Null for no child_results loaded yet, empty array for no child_results in home
    child_results: null,
  };
}

function setInitialChildrenAtHomeCheckIn(formikProps: any, children: SimpleChildSummary[]): void {
  const childrenAtAppointment = children.map((child) => {
    return {
      child: child.id,

      child_was_present: undefined,

      class_at_time: child.current_class,

      raw_score: undefined,
      index_score: undefined,
      percentage_score: undefined,
      description: undefined,
    } as ChildAtHomeToniResultPost;
  });
  formikProps.setFieldValue('child_results', childrenAtAppointment);
}

function getChildrenAtCheckInGrid(formikProps: any, childOptions: GenericOption[], mode: Mode) {
  const t = i18n.t;
  return (
    <FieldArray
      name={'child_results'}
      render={(arrayHelpers) => {
        const children = formikProps.values.child_results;
        return (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>{t('common.CHILD')}</th>
                <th>{t('toniResult.IS_PRESENT')}</th>
                <th>{t('toniResult.CLASS_AT_TIME')}</th>

                <th>{t('toniResult.SCORE')}</th>
              </tr>
            </thead>
            <tbody>
              {children && children.length > 0 ? (
                children.map((childOfHomeAppointment: ChildAtHomeToniResultPost, index: number) => (
                  <tr key={index}>
                    <td>
                      <FormikSelect name={`child_results[${index}].child`} options={childOptions} isReadOnly={true} />
                    </td>
                    <td>
                      <FormikSelect
                        name={`child_results[${index}].child_was_present`}
                        options={BOOLEAN_OPTIONS}
                        isReadOnly={mode === Mode.VIEW}
                      />
                    </td>
                    <td>
                      <FormikSelect
                        name={`child_results[${index}].class_at_time`}
                        options={CLASS_LEVEL_OPTIONS}
                        isReadOnly={mode === 'view'}
                      />
                    </td>
                    <td>
                      <FormikTextInput
                        label={t('toniResult.RAW_SCORE')}
                        name={`child_results[${index}].raw_score`}
                        formikProps={formikProps}
                        showSideBySide={true}
                        labelWidth={4}
                        isReadOnly={mode === Mode.VIEW}
                      />
                      <FormikTextInput
                        label={t('toniResult.INDEX_SCORE')}
                        name={`child_results[${index}].index_score`}
                        formikProps={formikProps}
                        showSideBySide={true}
                        labelWidth={5}
                        isReadOnly={mode === Mode.VIEW}
                      />
                      <FormikTextInput
                        label={t('toniResult.PERCENTAGE_SCORE')}
                        name={`child_results[${index}].percentage_score`}
                        formikProps={formikProps}
                        showSideBySide={true}
                        labelWidth={4}
                        isReadOnly={mode === Mode.VIEW}
                      />
                      <FormikSelect
                        label={t('toniResult.DESCRIPTION')}
                        name={`child_results[${index}].description`}
                        options={TONI_DESCRIPTION_OPTIONS}
                        labelWidth={4}
                        showSideBySide={true}
                        isReadOnly={mode === Mode.VIEW}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>No children in this home</td>
                </tr>
              )}
            </tbody>
          </Table>
        );
      }}
    />
  );
}

/*
  As of 2023-05-12 this is CREATE ONLY, they will be saved as Child Doctor Appointments
 */
const HomeToniResultPage = (props: HomeToniResultProps) => {
  let { mode: modeString, id: idString } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mode = parseMode(modeString);

  // const hasNumberId = !!idString && !Number.isNaN(parseInt(idString));
  // let id: number | undefined = undefined;
  // if (idString) {
  //   id = parseInt(idString);
  // }

  const [serverError, setServerError] = useState(false);
  const [selectedHome, setSelectedHome] = useState<number | undefined>(undefined);
  const { isLoadingPermissions, permissionMap } = usePermissions();
  const { data: homeData } = useGetHomes();
  const {
    data: childData,
    isFetching: isChildrenFetching,
    isIdle: isChildrenIdle,
  } = useGetChildrenInHome(selectedHome);

  // const { data, isLoading, error } = useGetHomeToniResultDetail(id, mode !== Mode.CREATE && hasNumberId);

  if (isLoadingPermissions) {
    return <Spinner animation="border" role="status" />;
  } else if (!permissionMap) {
    return <ErrorPage />;
  } else if (!hasPagePermission(mode, getDomainPermission(permissionMap, DOMAIN))) {
    return <NoPermissionPage />;
  }

  const homeOptions = createHomeOptions(homeData);
  const childOptions = createChildOptions(childData);

  let homeToniResult;
  let onSubmit;
  let onCancel: (resetForm: (dataToResetTo?: any) => void) => void;
  if (mode === Mode.CREATE) {
    homeToniResult = getBlankHomeCheckInNoChildren();
    onSubmit = async (data: HomeToniResultPost, { setSubmitting }: any) => {
      try {
        await postHomeToniResult(data);
        // TODO error handling?
        setSubmitting(false);
        navigate('../child-toni-result/search/');
      } catch (error) {
        setServerError(true);
      }
    };

    onCancel = (resetForm: () => void) => {
      resetForm();
      navigate('../child-toni-result/search');
    };
  } else if (serverError) {
    return <div> Something went wrong, contact LJI IT </div>;
  }
  // else if (hasNumberId) {
  //   if (isLoading || isHomesLoading || isChildrenFetching) {
  //     return <Spinner animation="border" role="status" />;
  //   } else if (!data) {
  //     return <div>Could not find data for {id}</div>;
  //   } else if (error) {
  //     return <div>Error loading {id}</div>;
  //  }
  //   homeToniResult = data as HomeToniResultPatch;
  //
  //   if (!selectedHome) {
  //     setSelectedHome(homeToniResult.home);
  //   }
  //
  //   onSubmit = async (data: HomeToniResultPatch, { setSubmitting }: any) => {
  //     try {
  //       // TODO where should I put code like this?
  //       // delete data.intake_photo;
  //       // delete data.intake_form_scan;
  //       await patchHomeToniResult(data.id, data);
  //       // TODO error handling?
  //       setSubmitting(false);
  //       navigate('../home-toni-result/view/' + idString);
  //     } catch (error) {
  //       setServerError(true);
  //     }
  //   };
  //   onCancel = (resetForm: () => void) => {
  //     resetForm();
  //     navigate('../home-toni-result/view/' + idString);
  //   };
  // }
  else {
    throw Error('Bad id: ' + idString);
  }

  function onBack() {
    navigate('../child-toni-result/search');
  }

  return (
    <Formik
      initialValues={homeToniResult}
      validationSchema={TONI_RESULT_FORM_SCHEMA}
      onSubmit={onSubmit}
      // Speeds up the form, but not sure what features I lose
      // TODO figure out how to speed up some other way.
      validateOnChange={false}
    >
      {(formikProps: FormikProps<HomeToniResultPost>) => {
        function onHomeChange(newHome: any) {
          setSelectedHome(newHome.value);
          formikProps.setFieldValue('child_results', null);
        }

        if (mode === Mode.CREATE) {
          if (!formikProps.values.child_results && childData) {
            // This seems like a bad place to set this state
            setInitialChildrenAtHomeCheckIn(formikProps, childData);
          }
        }
        return (
          <FormikForm>
            <Container>
              <Row>
                <Col>
                  <h1 className="text-center font-weight-bold">{t('toniResult.DETAIL_TITLE')}</h1>
                </Col>
              </Row>
              <Row>
                <Col sm={11}>
                  <FormikSelect
                    name={'home'}
                    label={t('common.HOME_NAME')}
                    onChange={onHomeChange}
                    options={homeOptions}
                    // isReadOnly={mode === Mode.VIEW}
                    labelWidth={2}
                    showSideBySide={true}
                  />
                  <FormikDateInput
                    name={'date'}
                    label={t('common.DATE')}
                    formikProps={formikProps}
                    // isReadOnly={mode === Mode.VIEW}
                    labelWidth={2}
                    showSideBySide={true}
                  />
                </Col>
                {/*<Col sm={1}>*/}
                {/*  <EditToggle*/}
                {/*    mode={mode}*/}
                {/*    formikProps={formikProps}*/}
                {/*    editUrl={'../home-toni-result/edit/' + idString}*/}
                {/*    onCancel={onCancel.bind(null, formikProps.resetForm)}*/}
                {/*  />*/}
                {/*</Col>*/}
              </Row>
              <Row>
                {isChildrenIdle && <div>Please pick a home</div>}
                {isChildrenFetching && <Spinner />}
                {formikProps.values.home &&
                  !isChildrenIdle &&
                  !isChildrenFetching &&
                  getChildrenAtCheckInGrid(formikProps, childOptions, mode)}
              </Row>
              <DefaultPageActionButtons
                mode={mode}
                formikProps={formikProps}
                onCancelEdit={onCancel.bind(null, formikProps.resetForm)}
                onBack={onBack}
              />
            </Container>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default HomeToniResultPage;
